import * as d3 from "d3";
import { Chart } from "./Chart";
import {
  NOT_APPLICABLE_COLOR_DOT,
  NEGATIVE_COLOR_DOT,
  NEGATIVE,
  INTERMEDIATE,
  INTERMEDIATE_COLOR_DOT,
  POSITIVE,
  POSITIVE_COLOR_DOT,
  RED_ON_RIGHT_COLOR_PALETTE,
  GREEN_ON_RIGHT_COLOR_PALETTE,
} from "../constants/ComparativeReportColorPalette.constants";
import {
  DEFAULT_RANGES,
  GRID_INTERVALS,
} from "../constants/ComparativeReport.constants";

export function ChartLollipop(
  alias,
  valueToDraw,
  chartData,
  ranges,
  ideals,
  showPercentage = true
) {
  this.alias = alias;
  this.chartData = chartData;
  this.showPercentage = showPercentage;
  this.total = 0;
  this.rangeMin = 0;
  this.rangeMax = 100;
  var reverse = false;
  // var tempRanges = ranges.call(this, d, i)

  var colorsToUse = RED_ON_RIGHT_COLOR_PALETTE;
  var rangez = DEFAULT_RANGES;
  if (!ranges) {
    colorsToUse = GREEN_ON_RIGHT_COLOR_PALETTE;
  }

  if (ranges) {
    rangez = [
      ranges.negMin1 === "Infinity" ? ranges.negMax1 * 1.2 : ranges.negMin1,
      ranges.negMax1,
      ranges.interMin1,
      ranges.interMax1,
      ranges.posMin1,
      ranges.posMax1,
      ranges.interMin2,
      ranges.interMax2,
      ranges.negMin2,
      ranges.negMax2 === "Infinity" ? ranges.negMin2 * 1.2 : ranges.negMax2,
    ];
  }
  var rangezDescending = rangez.slice().sort(d3.descending);
  var rangezAscending = rangez.slice().sort(d3.ascending);
  this.rangeMin = Math.min(rangezAscending[0]);
  this.rangeMax = Math.max(rangezDescending[0]);

  this.ranges = ranges;

  if (
    ideals &&
    ideals.length === 1 &&
    shouldDisplayGreenColorOnRight(ideals[0], rangezDescending[0])
  ) {
    colorsToUse = GREEN_ON_RIGHT_COLOR_PALETTE;
  }

  var chart = new Chart(alias);

  this.render = function () {
    // X axis

    var x1 = d3
      .scaleLinear()
      .domain([this.rangeMin, this.rangeMax])
      .range(reverse ? [chart.chartWidth, 0] : [0, chart.chartWidth]);

    chart.svg
      .append("g")
      .attr("transform", "translate(0," + chart.chartHeight + ")")
      .call(d3.axisBottom(x1));

    this.__chart__ = x1;

    var range = chart.svg
      .append("g")
      .selectAll("rect.range")
      .data(rangezDescending);

    // Add Y axis
    var y = d3
      .scaleBand()
      .range([0, chart.chartHeight])
      .domain(
        this.chartData.map(function (s) {
          return s.businessName;
        })
      )
      .padding(1);

    chart.svg
      .append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("text-anchor", "end")
      .call(wrap, 110);

    // Retrieve the old x-scale, if this is an update.
    var x0 =
      this.__chart__ ||
      d3.scaleLinear().domain([0, Infinity]).range(x1.range());
    // Derive width-scales from the x-scales.
    var w0 = backgroundWidth(x0, 0);

    if (isNaN(w0)) {
      w0 = 0;
    }

    var w1 = backgroundWidth(x1, this.rangeMin);

    range
      .enter()
      .append("rect")
      // chart.svg.append('rect')
      .style("fill", function (d, i) {
        // return colorsToUse[i]
        return colorsToUse[i];
      })
      .attr("width", w0)
      .attr("height", chart.chartHeight)
      .attr("x", reverse ? x0 : 0)
      .transition()
      .duration(0)
      .attr("width", w1)
      .attr("x", reverse ? x1 : 0);

    GRID_INTERVALS.forEach((positionValue) => {
      chart.svg
        .append("line")
        .attr("x1", x1(positionValue))
        .attr("x2", x1(positionValue))
        .attr("y1", chart.chartHeight)
        .attr("y2", 0)
        .attr("stroke-width", 1)
        .attr("stroke", () => {
          return "white";
        });
    });

    chartData.forEach((s) => {
      chart.svg
        .append("line")
        .attr("x1", 0)
        .attr("x2", x1(100))
        .attr("y1", y(s.businessName))
        .attr("y2", y(s.businessName))
        .attr("stroke-width", 1)
        .attr("stroke", () => {
          return "white";
        });
    });

    // Lines top point
    chart.svg
      .selectAll("myline")
      .data(this.chartData)
      .enter()
      .append("line")
      .attr("x1", function (d) {
        if (d[valueToDraw] === "N/A") {
          return 0;
        }
        return x1(d[valueToDraw]);
      })
      .attr("x2", function (d) {
        if (d[valueToDraw] === "N/A") {
          return 0;
        }
        return x1(d[valueToDraw]);
      })
      .attr("y1", chart.chartHeight)
      .attr("y2", 0)
      .attr("stroke-width", 1)
      .attr("stroke", (d) => {
        return getColorPoint(d[valueToDraw]);
      });

    // Circles
    chart.svg
      .selectAll("mycircle")
      .data(this.chartData)
      .enter()
      .append("circle")
      .attr("cx", function (d) {
        if (d[valueToDraw] === "N/A") {
          return 0;
        }
        return x1(d[valueToDraw]);
      })
      .attr("cy", function (s) {
        return y(s.businessName);
      })
      .attr("r", "8")
      .attr("fill", (d) => {
        return getColorPoint(d[valueToDraw]);
        // }
      })
      .on("mouseover", (d) => {
        const numericValue = Number(d[valueToDraw]);
        const stringValue = isNaN(numericValue)
          ? "N/A"
          : numericValue.toFixed(2);
        chart.openTooltip(
          d.businessName +
            " - " +
            stringValue +
            (stringValue !== "N/A" && showPercentage ? "%" : "")
        );
      })
      .on("mouseout", function () {
        chart.closeTooltip();
      })
      .on("mousemove", function () {
        var coordinates = d3.mouse(this);
        chart.moveTooltip(coordinates[0], coordinates[1] - 30);
      });
  };
  function wrap(text, width) {
    text.each(function () {
      var text = d3.select(this);
      var words = text.text().split(/\s+/).reverse();
      var word;
      var line = [];
      var lineNumber = 0;
      var lineHeight = 1; // ems
      var x = text.attr("x");
      var y = text.attr("y");
      var dy = 0; // parseFloat(text.attr("dy")),
      var tspan = text
        .text(null)
        .append("tspan")
        .attr("x", x)
        .attr("y", y)
        .attr("dy", dy + "em");
      // eslint-disable-next-line no-cond-assign
      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];

          if (lineNumber < 1) {
            tspan = text
              .append("tspan")
              .attr("x", x)
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
          }
        }
      }
    });
  }
  this.refresh = function () {
    // Lines top point
    var x = d3.scaleLinear().domain([100, 0]).range([chart.chartHeight, 0]);

    // Add Y axis
    var y = d3
      .scaleBand()
      .range([0, chart.chartHeight])
      .domain(
        this.chartData.map(function (s) {
          return s.businessName;
        })
      )
      .padding(1);

    chart.svg.selectAll("myline").remove();

    // Circles
    chart.svg.selectAll("mycircle").remove();
    chart.svg
      .selectAll("mycircle")
      .data(this.chartData)
      .enter()
      .append("circle")
      .attr("cx", function (d) {
        return x(d[valueToDraw]);
      })
      .attr("cy", function (s) {
        return y(s.businessName);
      })
      .attr("r", "8")
      .attr("fill", (d) => {
        return getColorPoint(d[valueToDraw]);
      });
  };

  var getColorPoint = function (index, containsError) {
    if (
      containsError === true ||
      index === undefined ||
      index === null ||
      index === "N/A"
    ) {
      return NOT_APPLICABLE_COLOR_DOT;
    }

    if (!ranges) {
      return getPillarInterval(index);
    }
    switch (getIndicatorColor(index)) {
      case NEGATIVE:
        return NEGATIVE_COLOR_DOT;
      case INTERMEDIATE:
        return INTERMEDIATE_COLOR_DOT;
      case POSITIVE:
        return POSITIVE_COLOR_DOT;
    }
  };

  function getPillarInterval(value) {
    if (value >= rangez[0] && value <= rangez[1]) {
      return NEGATIVE_COLOR_DOT;
    }

    if (value > rangez[2] && value <= rangez[3]) {
      return INTERMEDIATE_COLOR_DOT;
    }

    if (value > rangez[4] && value <= rangez[5]) {
      return POSITIVE_COLOR_DOT;
    }

    return NOT_APPLICABLE_COLOR_DOT;
  }

  function getIndicatorColor(value) {
    if (!ranges) {
      return NEGATIVE;
    }
    if (value >= ranges.negMin1 && value <= ranges.negMax1) {
      return NEGATIVE;
    }

    if (
      (value >= ranges.interMin1 && value <= ranges.interMax1) ||
      (ranges.interMax2 &&
        ranges.interMin2 &&
        value >= ranges.interMin2 &&
        value <= ranges.interMax2)
    ) {
      return INTERMEDIATE;
    }

    if (value >= ranges.posMin1 && value <= ranges.posMax1) {
      return POSITIVE;
    }

    return NEGATIVE;
  }

  function backgroundWidth(x, min) {
    var x0 = x(min);
    return function (d) {
      const actualWidth = Math.abs(x(d) - x0);
      return actualWidth;
    };
  }

  function shouldDisplayGreenColorOnRight(idealValue, endOfPillar) {
    if (idealValue === endOfPillar) {
      return true;
    }

    return false;
  }
}
